import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import qs from 'qs';

import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import { Loader } from 'semantic-ui-react';
import {
  filter, get, head, isBoolean, isEmpty,
} from 'lodash';
import {
  arrayOf, bool, func, shape, string,
} from 'prop-types';
import {
  clearSurveyQuestions,
  setSurveyPageQuestions,
  setSurveyPageAnswers,
} from '../../../core/actions';

import {
  RedirectModal,
} from './components/redirect-modal';

import SurveyQuestions from './questions';

import { PROJECT_WITH_SURVEY_PAGES } from '../../../core/graphql/survey';
import { GET_TEST_SESSION_STATUS } from '../../../core/graphql/test-sessions';

import {
  Container, Text, ContainerError,
} from './styles';
import { handleRedirectSession } from '../../utils/handleRedirectSession';
import { Message } from '../recruit-participants/styles';
import { useLastPage } from '../../../core/hooks/use-last-page';
import { buildDisplayLogic } from './helpers';

const Survey = (props) => {
  const {
    history,
    location,
    onClearSurveyQuestions,
    onSetSurveyPageQuestions,
    onSetSurveyPageAnswers,
    survey,
  } = props;

  const {
    currentSurveyPage = {},
    isLastSurveyPage,
    surveyPages = [],
    testSession = {},
    project: projectReducer = {},
    error: surveyError,
  } = survey;

  const projectId = projectReducer?.id;
  const testSessionId = testSession?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { search } = location;
  const { isPreviewStudy: isPreviewStudyString } = qs.parse(search, { ignoreQueryPrefix: true });
  const isPreviewStudy = isBoolean(isPreviewStudyString) ? isPreviewStudyString : isPreviewStudyString === 'true';
  const surveyPagesAnswers = useSelector((state) => state.survey.surveyPagesAnswers || []);
  const [surveyPageAnswersError, setSurveyPageAnswersError] = useState(false);

  const [allQuestions, setAllQuestions] = useState([]);

  const {
    data = {},
    error,
    loading,
  } = useQuery(PROJECT_WITH_SURVEY_PAGES, {
    variables: {
      id: projectId,
    },
    skip: isEmpty(projectId),
  });

  const {
    data: dataTestSession = {},
    loading: loadingData,
  } = useQuery(GET_TEST_SESSION_STATUS, {
    variables: {
      testSessionId,
    },
    skip: isEmpty(testSessionId),
  });

  const { testSessions = {} } = dataTestSession || {};
  const { project = {} } = data || {};
  const {
    thankYouRedirectLink,
    thankYouTitle,
    thankYouContent,
    hasMrId,
  } = project || {};

  const treatedAccumulatedAnswers = useMemo(() => surveyPagesAnswers.reduce(
    (acc, pageAnswers) => ([...acc, ...pageAnswers]),
    [],
  ),
  [surveyPagesAnswers]);

  const isLastPageState = useLastPage(
    isLastSurveyPage,
    allQuestions,
    surveyPagesAnswers,
    surveyPages,
    currentSurveyPage,
    treatedAccumulatedAnswers,
  );

  const handleCompleteSession = useCallback(() => {
    if (!thankYouRedirectLink) {
      history.push({
        pathname: '/thank-you',
        state: {
          title: thankYouTitle,
          content: thankYouContent,
        },
      });
    } else {
      setIsRedirecting(true);
      const testSessionMrId = testSessions[0]?.tester?.mrId;
      handleRedirectSession(thankYouRedirectLink, hasMrId, testSessionMrId);
    }

    onClearSurveyQuestions();
  }, [hasMrId, history, onClearSurveyQuestions, testSessions, thankYouContent, thankYouRedirectLink, thankYouTitle]);

  const handleTesterSubmit = async (values) => {
    setIsLoading(true);

    try {
      // Filter out questions that have not been answered because of question display logic
      const filteredValues = values.filter((item) => {
        if (item.questionOptions && item.questionOptions.length === 0 && item.questionType !== 'written') {
          return true;
        }
        return item.answer && Object.keys(item.answer).length > 0;
      });

      if (!isEmpty(filteredValues) || isPreviewStudy) {
        const { sessionCompleted } = await onSetSurveyPageAnswers(filteredValues, isPreviewStudy, isLastPageState);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        if (sessionCompleted) {
          handleCompleteSession();
        }
      }
    } catch (e) {
      captureException('HANDLE_TESTER_SUBMIT_FAILED', e);
      setSurveyPageAnswersError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const questions = currentSurveyPage?.questions || [];

    // Get list of controlled question IDs
    const controlledQuestionIds = [];
    if (currentSurveyPage.displayQuestion && currentSurveyPage.displayQuestion.conditions) {
      currentSurveyPage.displayQuestion.conditions.forEach((condition) => {
        controlledQuestionIds.push(...condition.questionsList);
      });
    }

    const initialQuestions = questions.map((question) => {
      const isControlled = controlledQuestionIds.includes(question.id);
      // If no controlledQuestionIds, default to true
      const isVisible = controlledQuestionIds.length > 0 ? !isControlled : true;
      return {
        ...question,
        isVisible,
      };
    });

    const questionVisibility = buildDisplayLogic(treatedAccumulatedAnswers, surveyPages);

    // Update 'isVisible' for each question
    const questionsWithVisibility = initialQuestions.map((q) => ({
      ...q,
      isVisible: questionVisibility[q.id] !== undefined ? questionVisibility[q.id] : q.isVisible,
    }));

    // Update allQuestions
    setAllQuestions(questionsWithVisibility);
  }, [currentSurveyPage, treatedAccumulatedAnswers, surveyPages]);

  useEffect(() => {
    if (isEmpty(currentSurveyPage) && isLastSurveyPage) {
      handleCompleteSession();
    }

    if (isEmpty(currentSurveyPage) && !isEmpty(project)) {
      const sortRandom = get(project, 'sortRandom', false);
      const surveys = get(project, 'surveys', []);
      const surveySession = filter(surveys, (v) => v.surveyType === 'session');
      const firstSurvey = head(surveySession);
      const surveysPages = get(firstSurvey, 'surveysPages', []);

      onSetSurveyPageQuestions(surveysPages, sortRandom);
    }
  }, [data, loading, onSetSurveyPageQuestions, project, currentSurveyPage, isLastSurveyPage, handleCompleteSession]);

  if (!isEmpty(testSessions) && head(testSessions).status === 'processed') {
    return (
      <Container>
        <Text>
          All the questions have been answered!
        </Text>
      </Container>
    );
  }

  if (error || surveyPageAnswersError) {
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="Something went wrong."
        />
      </Container>
    );
  }

  if (testSessions[0] && new Date(testSessions[0].expiresAt) < new Date()) {
    return (
      <ContainerError center>
        <Message>Test session expired</Message>
      </ContainerError>
    );
  }

  return (
    <>
      {(loadingData || isLoading || isRedirecting) ? (
        <Loader active data-loader="survey-loader" />
      ) : (
        <>
          <Container>
            <SurveyQuestions
              isLastPage={isLastPageState}
              loading={isLoading}
              onSubmit={handleTesterSubmit}
              page={currentSurveyPage}
              pagesList={surveyPages}
              surveyError={surveyError}
              allQuestions={allQuestions}
              setAllQuestions={setAllQuestions}
              treatedAccumulatedAnswers={treatedAccumulatedAnswers}
            />

            {
              isRedirecting
              && <RedirectModal />
            }

            {surveyError && (
              <ContainerError>
                <Text>
                  Something went wrong while saving the answers. Try again.
                </Text>
              </ContainerError>
            )}
          </Container>
        </>
      )}
    </>
  );
};

Survey.propTypes = {
  history: shape({
    push: func,
  }).isRequired,
  location: shape({
    search: string,
    pathname: string,
  }).isRequired,
  onClearSurveyQuestions: func,
  onSetSurveyPageQuestions: func,
  onSetSurveyPageAnswers: func,
  survey: shape({
    currentSurveyPage: shape({}),
    isLastSurveyPage: bool.isRequired,
    surveyPages: arrayOf(shape({})),
    project: shape({
      id: string,
      thankYouRedirectLink: string,
      thankYouTitle: string,
      thankYouContent: string,
      hasMrId: bool,
    }),
    testSession: shape({
      id: string,
    }),
    error: shape({
      message: string,
    }),
  }),
};

Survey.defaultProps = {
  onClearSurveyQuestions: () => { },
  onSetSurveyPageQuestions: () => { },
  onSetSurveyPageAnswers: () => { },
  survey: {
    questions: [],
    surveyPages: [],
    isLastSurveyPage: false,
    currentSurveyPage: {},
    maxSurveyPages: 1,
    testSession: {},
  },
};

const mapStateToProps = ({ survey }) => ({
  survey,
});

const mapDispatchToProps = {
  onClearSurveyQuestions: clearSurveyQuestions,
  onSetSurveyPageQuestions: setSurveyPageQuestions,
  onSetSurveyPageAnswers: setSurveyPageAnswers,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(Survey);
